/**
 * ResubscribeSection.tsx — the "Re-subscribe" block on the ADMIN subscription
 * drill-down.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: end-of-term re-subscribe.
 *
 * WHY IT LIVES ON THE DRAWER. This is the one screen that already answers "what
 * happened on this plan" — the order, the payments, the prescriptions, the
 * pharmacy sends. "Can this patient have another term, and start one" is the
 * next question an admin asks while looking at exactly that, so it belongs here
 * rather than behind a separate console section.
 *
 * ELIGIBILITY IS THE SERVER'S ANSWER, NOT OURS. `detail.resubscribe` is computed
 * by the same function the write uses, so the button and the write can never
 * disagree. Three distinct renders, and the difference between the last two
 * matters:
 *   • key ABSENT → nothing at all. An orders revision predating this lane cannot
 *     answer the question, and a disabled button we cannot explain is worse than
 *     no button.
 *   • `eligible: false` → the control, disabled, WITH the server's reason in
 *     words. An admin on the phone to a patient needs to know why, not just
 *     that they cannot.
 *   • `eligible: true` → the control, live.
 *
 * WHAT THE ADMIN SEES AFTERWARDS. A lane-B initiate is not the end of the story
 * — the patient still has to confirm — so the created offer stays on screen with
 * its state, and a lane-A or self-serve confirm that stalled on the card shows
 * the honest reason plus the payment link the patient was sent. Withdrawing is
 * offered from here too, behind the same reason-gated confirmation as a refund.
 *
 * WHY THIS DOES NOT ASK THE DRAWER TO REFETCH after a write. The drawer's reload
 * swaps the whole panel for its skeleton, which would UNMOUNT this block and
 * throw away the very thing the admin just did — they would press "Start a new
 * plan", see a flicker, and be left looking at the button again with no idea
 * whether it worked. The server's answer to the write IS the newer truth for
 * this block, so we render that and let the next open of the drawer refetch.
 *
 * R28: category-level copy only — no treatment is named on this screen.
 */
import { useState } from 'react';
import { isResubscribeOffer, withdrawResubscribeOffer } from '../api/orders';
import type {
  ResubscribeEligibility,
  ResubscribeInitiateResult,
  StaffSubscription,
} from '../api/types';
import { formatPence, formatUtcDate } from '../lib/format';
import {
  FAILURE_REASON_COPY_ADMIN,
  INELIGIBLE_REASON_COPY,
  OFFER_STATE_COPY,
  monthsLabel,
} from '../resubscribe/resubscribeCopy';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { SectionTitle } from '../ui/sections';
import { Alert, Badge, Button, Help } from '../ui/kit';
import { ResubscribeDialog } from './ResubscribeDialog';

/** Offer states that are over — nothing left to withdraw or wait for. */
const TERMINAL_STATES = new Set(['confirmed', 'expired', 'declined', 'withdrawn']);

export function ResubscribeSection({
  subscription,
  eligibility,
}: {
  subscription: StaffSubscription;
  /** The server's answer. UNDEFINED means "this server cannot answer", never "no". */
  eligibility?: ResubscribeEligibility;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  // What the initiate answered with, created in THIS drawer session. The drawer
  // is keyed by subscription id and remounts per plan, so this never leaks
  // between patients. It is the UNION, not the offer view: lane A answers with a
  // confirm result, and reading `months`/`monthlyPence` off that renders NaN.
  const [offer, setOffer] = useState<ResubscribeInitiateResult | null>(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);

  // A server that does not know about re-subscribe says nothing, and so do we.
  if (!eligibility) return null;

  function confirmWithdraw(reason?: string) {
    if (!offer || !reason) return;
    setWithdrawBusy(true);
    setWithdrawError(null);
    withdrawResubscribeOffer(offer.offerId, reason)
      .then((updated) => {
        setWithdrawBusy(false);
        setWithdrawOpen(false);
        setOffer(updated);
      })
      .catch(() => {
        setWithdrawBusy(false);
        setWithdrawOpen(false);
        setWithdrawError('Sorry — the offer could not be withdrawn. Please try again.');
      });
  }

  return (
    <div>
      <SectionTitle>Re-subscribe</SectionTitle>

      {offer ? (
        <OfferPanel offer={offer} />
      ) : eligibility.eligible ? (
        <Help style={{ marginBottom: 'var(--gps-s-3)' }}>
          This plan has run its term. You can start a new one — by default the patient confirms it themselves
          before anything is charged.
        </Help>
      ) : (
        <Alert tone="info" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
          {eligibility.reason ? INELIGIBLE_REASON_COPY[eligibility.reason] : 'This plan cannot be re-subscribed.'}
        </Alert>
      )}

      {withdrawError ? (
        <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
          {withdrawError}
        </Alert>
      ) : null}

      {offer ? (
        // Only a live offer can be withdrawn; a confirmed / expired / declined /
        // withdrawn one has nothing left to stop.
        TERMINAL_STATES.has(offer.state) ? null : (
          <Button type="button" variant="ghost" onClick={() => setWithdrawOpen(true)}>
            Withdraw this offer
          </Button>
        )
      ) : (
        <Button
          type="button"
          variant="ghost"
          disabled={!eligibility.eligible}
          onClick={() => setDialogOpen(true)}
        >
          Start a new plan
        </Button>
      )}

      <ResubscribeDialog
        open={dialogOpen}
        priorSubscriptionId={subscription.subscriptionId}
        priorMonths={subscription.months}
        priorMonthlyPence={subscription.monthlyPence}
        onDone={(created) => {
          setDialogOpen(false);
          setOffer(created);
        }}
        onCancel={() => setDialogOpen(false)}
      />

      <ConfirmDialog
        open={withdrawOpen}
        title="Withdraw this offer?"
        tone="danger"
        confirmLabel="Withdraw"
        busy={withdrawBusy}
        requireReason
        reasonLabel="Reason for withdrawing"
        body={
          <>
            The patient’s confirmation link stops working and the pending order is cancelled. Nothing has been
            charged, so there is nothing to refund. This is recorded against your name.
          </>
        }
        onConfirm={confirmWithdraw}
        onCancel={() => {
          if (!withdrawBusy) setWithdrawOpen(false);
        }}
      />
    </div>
  );
}

/**
 * What happened to the offer we just created.
 *
 * TWO SHAPES ARRIVE HERE, and the difference is not cosmetic. Lane B answers
 * with the full offer view; lane A runs the confirm inline and answers with a
 * CONFIRM RESULT, which carries the state and the failure reason but no `lane`,
 * `months`, `monthlyPence` or authorisation read-back. Every offer-only field
 * below is therefore rendered only behind the narrowing — the alternative,
 * reading them straight off the union, printed "undefined months at £NaN" to an
 * admin who had just authorised a charge on a patient's behalf.
 *
 * The missing figures are NOT reconstructed from the dialog's inputs: the term
 * is the admin's choice but the price is resolved server-side at initiate, so
 * anything we printed here would be a guess at the number actually held. Saying
 * less is the honest option until orders returns the offer view on both lanes.
 *
 * `payment_action_required` is the state that needs care: it is an honest stall,
 * not a failure. Nothing was captured and no ledger row was written — the
 * patient simply has to finish with a card, which is what the link is for. All
 * three underlying reasons (a bank asking for authentication, a declined card,
 * no card on file) share that one remedy, which is why they share one state.
 */
function OfferPanel({ offer }: { offer: ResubscribeInitiateResult }) {
  const stalled = offer.state === 'payment_action_required';
  const full = isResubscribeOffer(offer) ? offer : null;
  return (
    <div style={{ marginBottom: 'var(--gps-s-3)' }}>
      <p style={{ margin: '0 0 8px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
        <Badge tone={stalled ? 'danger' : offer.state === 'confirmed' ? 'positive' : 'pending'}>
          {OFFER_STATE_COPY[offer.state]}
        </Badge>
        {full ? (
          <span style={{ fontSize: '13.5px', color: 'var(--gps-text)' }}>
            {monthsLabel(full.months)} at {formatPence(full.monthlyPence)} a month
          </span>
        ) : null}
      </p>

      {full && full.state === 'pending_confirmation' ? (
        <Help style={{ marginBottom: 'var(--gps-s-2)' }}>
          We have emailed the patient a confirmation link. It expires {formatUtcDate(full.expiresAt)}.
        </Help>
      ) : null}

      {stalled ? (
        <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-2)' }}>
          {offer.failureReason
            ? FAILURE_REASON_COPY_ADMIN[offer.failureReason]
            : 'The payment could not be taken automatically.'}{' '}
          We have emailed the patient a payment link so they can finish with any card.
        </Alert>
      ) : null}

      {full?.lane === 'admin_authorised' && full.authorisationTakenAt ? (
        // Read back what was recorded, so a mistyped date is caught here rather
        // than by whoever reads the audit trail months later.
        <Help style={{ marginBottom: 0 }}>
          Recorded as authorised by the patient on {formatUtcDate(full.authorisationTakenAt)}.
        </Help>
      ) : null}
    </div>
  );
}
